<template>
  <StepperTemplate :returnRoute="{ to: supportFormRoute }">
    <template v-slot:title>
      <div class="mb-2 py-3 py-md-2">
        <div class="h2-stepper-title font-weight-bold text-blue-500">
          {{ $t('donorForm.headline') }}
        </div>
        <b-icon-info-circle variant="blue-500" class="mr-2 mt-1"></b-icon-info-circle>
        <span class="text-base text-blue-500">{{ $t('donorForm.subheadline') }}</span>
      </div>
    </template>
    <template v-slot:body>
      <div class="stepper-body-card">
        <DonorData class="my-2" />
        <DonorConsents
          v-if="resourceLoader === false"
          :consentCheckboxes="getConsentCheckboxExclude(EXCLUDE_CHECKBOX_ID)"
        />
        <b-row v-if="supportInfoText" class="my-3" align-h="between" no-gutters v-orphans>
          <b-col cols="12">
            <b-row>
              <b-col cols="auto" class="pr-0">
                <b-icon-info-circle variant="blue-500" />
              </b-col>
              <b-col>
                <p class="d-inline text-blue-500 text-md">
                  {{ supportInfoText }}
                </p>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row :align-h="align">
          <b-col cols="12" md="auto" class="py-1 py-md-0">
            <p class="text-center text-md-right text-blue-500 mb-1 text-md">
              {{ addValueText }}
              <span class="font-weight-bold">
                {{ selectedAmountText }}
                {{ $t('form.label.pln') }}
              </span>
            </p>
            <div>
              <span class="font-responsive">
                <b-button
                  variant="salmon-dark"
                  block
                  class="text-base-mobile-lg font-weight-bold py-2 mt-0 mt-md-3 line-height-lg angle-right-icon-hover"
                  :disabled="resourceLoader || sendLoader || isTemporarilyDisabled"
                  @click="submit"
                >
                  {{ getButtonText }}
                  <i class="angle-right-icon ml-1 bg-white"></i>
                  <b-spinner v-if="sendLoader" small class="ml-2"></b-spinner>
                </b-button>
              </span>
            </div>
            <FormSteps class="d-md-none steps-mobile" />
            <div
              v-if="methodType === PaymentMethod.online"
              class="d-flex justify-content-center my-3"
            >
              <SecurePayment />
            </div>
          </b-col>
        </b-row>
        <div v-if="getConsentText" class="mt-4">
          <div
            v-for="(text, index) in getConsentText"
            v-html="text.options.label"
            :key="index"
            class="fs-08 custom-checkbox-consent"
          ></div>
        </div>
        <ScrollTopButton class="scroll-top-bottom-form"></ScrollTopButton>
        <ModalChildrenChosen v-if="isChildChosen" :isChildChosen="isChildChosen">
        </ModalChildrenChosen>
        <ModalChildrenBadRequest
          v-if="isBadRequest"
          :isBadRequest="isBadRequest"
          :timestamp="badRequestTimestamp"
        >
        </ModalChildrenBadRequest>
      </div>
    </template>
  </StepperTemplate>
</template>

<script setup lang="ts">
import DonorConsents from '@/components/donorDataPage/DonorConsents.vue'
import DonorData from '@/components/donorDataPage/DonorData.vue'
import StepperTemplate from '@/components/widgets/StepperTemplate.vue'
import ModalChildrenChosen from '@/components/childPage/ModalChildrenChosen.vue'
import ModalChildrenBadRequest from '@/components/formOfPayment/ModalChildrenBadRequest.vue'
import SecurePayment from '@/components/formOfPayment/SecurePayment.vue'

import useResourceStore from '@/stores/resource'
import useDataLayerStore from '@/stores/dataLayer'

import { storeToRefs } from 'pinia'

import ScrollTopButton from '@/components/widgets/ScrollTopButton.vue'

import FormSteps from '@/components/widgets/FormSteps.vue'

import scrollIntoInvalidField from '@/helpers/scrollIntoInvalidField'

import { useButtonAlign, useButtonDelay } from '@/composables'
import { useConsent } from '@/composables/useConsent'
import { useSupportText } from '@/composables'
import useTransactionDataStore from '@/stores/transactionData'
import { computed, ref } from 'vue'
// @ts-ignore
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import axios from 'axios'
import { useFieldValue } from 'vee-validate'
import { PaymentMethod } from '@/types'
import type { Maybe } from '@/types'

const router = useRouter()
const route = useRoute()

const { align } = useButtonAlign()
const { isTemporarilyDisabled } = useButtonDelay()

const transactionDataStore = useTransactionDataStore()
const { initUseForm } = transactionDataStore
const { methodType, paymentType, amount } = storeToRefs(transactionDataStore)
const { handleSubmit } = initUseForm()

const childEditionId = computed<string | string[]>(() => route.params.childEditionId)

const supportFormRoute = computed<RouteLocationRaw>(() => ({
  name: 'FormOfSupport',
  params: { childEditionId: childEditionId.value }
}))

const resourceStore = useResourceStore()
const { getConsentsLoader: resourceLoader } = storeToRefs(resourceStore)

const { getConsentCheckboxExclude, EXCLUDE_CHECKBOX_ID, getConsentText } = useConsent()

function submit() {
  handleSubmit(
    () => {
      send()
    },
    () => {
      scrollIntoInvalidField()
    }
  )()
}

await resourceStore.fetchConsents()

const isChildChosen = ref<boolean>(false)
const isBadRequest = ref<boolean>(false)
const badRequestTimestamp = ref<Maybe<string>>(null)
const MODAL_RESPONSE_CHILD_CHOSEN_STATUS = 403
const MODAL_RESPONSE_BAD_REQUEST_STATUS = 400
const sendLoader = ref<boolean>(false)

const dataLayerStore = useDataLayerStore()
const vuexStore = useStore()
const send = async () => {
  sendLoader.value = true
  try {
    const { data } = await vuexStore.dispatch('payment/send')
    const paymentData = data?.data
    const redirectUrl = paymentData?.p24_url
    dataLayerStore.saveDataForDataLayer(paymentData?.id, paymentData?.installments_id)

    if (methodType.value === PaymentMethod.traditional) {
      vuexStore.commit('payment/setPaymentId', paymentData?.id ?? null)
      router.push({ name: 'SummaryBankTransfer' })
    } else {
      window.location.assign(redirectUrl)
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const { status, data } = err.response ?? {}
      isChildChosen.value = status === MODAL_RESPONSE_CHILD_CHOSEN_STATUS
      isBadRequest.value = status === MODAL_RESPONSE_BAD_REQUEST_STATUS
      badRequestTimestamp.value = isBadRequest.value ? data?.data?.timestamp : null
    }
  } finally {
    sendLoader.value = false
  }
}

const { t } = useI18n()
const getButtonText = computed(() => {
  return methodType.value === PaymentMethod.traditional
    ? t('form.formOfPayment.paymentTraditional')
    : t('form.formOfPayment.payment')
})
const otherAmount = useFieldValue('otherAmount')
const selectedAmountText = computed(() => {
  return amount.value === -1 ? otherAmount.value : amount.value
})
const { addValueTextByType, supportInfoTextByType } = useSupportText()
const addValueText = computed<string>(() => {
  return addValueTextByType(paymentType.value)
})
const supportInfoText = computed<string>(() => {
  return supportInfoTextByType(paymentType.value)
})
</script>
